import {
  Component,
  ElementRef,
  OnInit,
  TemplateRef,
  computed,
  inject,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { catchError, concatMap, filter, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { CouponDetailComponent } from '../coupon-detail/coupon-detail.component';
import { CouponInputComponent } from '../coupon-input/coupon-input.component';
import { RoundIconComponent } from '../round-icon/round-icon.component';
import { IconComponent } from '../icons/icon.component';
import { LayoutService } from '../../core/services/layout.service';
import { AuthService } from '../../core/services/auth.service';
import { OrderService } from '../../core/services/order.service';
import { ErrorsService } from '../../core/services/errors.service';
import { AccountCouponsService } from '../../core/services/account/account-coupons.service';
import { IconNameType } from '../icons/icon-name-type.enum';
import { Coupon } from '../../core/models/core/coupon.model';
import { OrderSpecialService } from '../../core/services/ordering/order-special.service';
import { Adjustment } from '../../core/models/ordering/adjustment.model';
import { OrderedSpecial } from '../../core/models/ordering/ordered-special.model';
import { CouponStatus } from '../../core/models/core/coupon-status.enum';
import { openClose } from '../animations/animations';
import { ConfigService } from '../../core/services/config.service';
import { CouponType } from '../../core/models/core/coupon-type.enum';
import { ModalService, ModalName } from '../../core/services/modal.service';
import { LoyaltyService } from '../../core/services/loyalty.service';
import { CouponOfferType } from '../../core/models/core/coupon-offer-type.enum';
import { SpecialWizardService } from '../../core/services/special-wizard.service';
import { MenuService } from '../../core/services/menu.service';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { EcommerceTriggeringArea } from '../../core/models/ecommerce/ecommerce-triggering-area.enum';
import { EcommerceTriggeringPoint } from '../../core/models/ecommerce/ecommerce-triggering-point.enum';
import { ToastService } from '../../core/services/toast.service';
import { AuditService } from '../../core/services/audit.service';
import { CouponConflictComponent } from "../coupon-conflict/coupon-conflict.component";
import { OrderWarningComponent } from "../order-warning/order-warning.component";
import { OrderWarning } from '../../core/models/ordering/order-warning.model';
import { OrderWarningType } from '../../core/models/ordering/order-warning-type.enum';

@Component({
  selector: 'cwo-add-coupon-modal',
  standalone: true,
  imports: [
    CouponDetailComponent,
    CouponInputComponent,
    RoundIconComponent,
    IconComponent,
    CommonModule,
    TranslocoDirective,
    CouponConflictComponent,
    OrderWarningComponent
],
  templateUrl: './add-coupon-modal.component.html',
  styleUrl: './add-coupon-modal.component.scss',
  animations: [openClose()],
})
export class AddCouponModalComponent implements OnInit{
  modalService = inject(ModalService);
  layoutService = inject(LayoutService);
  authService = inject(AuthService);
  orderService = inject(OrderService);
  orderSpecialService = inject(OrderSpecialService);
  errorsService = inject(ErrorsService);
  accountCouponsService = inject(AccountCouponsService);
  configService = inject(ConfigService);
  loyaltyService = inject(LoyaltyService);
  menuService = inject(MenuService);
  specialWizardService = inject(SpecialWizardService);
  translocoService = inject(TranslocoService);
  toastService = inject(ToastService);
  couponWarning: Coupon | null = null;

  private auditService = inject(AuditService);
  loadingCouponIndex = -1;

  order = computed(() => {
    return this.orderService.order();
  });
  coupons = computed(() => {
    return this.configService.configState().store?.hasLoyalty
      ? this.accountCouponsService.coupons()
      : this.accountCouponsService
          .coupons()
          .filter((coupon) => coupon.type !== CouponType.LOYALTY);
  });

  availableCoupons = computed(() => {
    return this.coupons().filter(
      (coupon) => coupon.status !== CouponStatus.NON_APPLICABLE
    );
  });

  unavailableCoupons = computed(() => {
    return this.coupons().filter(
      (coupon) => coupon.status === CouponStatus.NON_APPLICABLE
    );
  });

  accountState = computed(() => this.authService.state().account);

  storeState = computed(() => this.configService.configState().store);

  unsubmittedCoupon = computed(() => {
    return this.accountCouponsService.unsubmittedCoupon();
  });

  iconNameType = IconNameType;
  isTempApplied: boolean = false;
  allErrors?: string[] = [];
  couponInputErrors?: string[] = [];
  couponWarningApplicationFailed?: OrderWarning;
  couponResponse?: Coupon;


  readonly couponFormContainer = viewChild.required<ElementRef<HTMLElement>>('couponFormContainer');
  private readonly couponWarningModalTemplateRef = viewChild.required<TemplateRef<any>>('couponWarningModal');
  private readonly couponInstructionModalTemplateRef = viewChild.required<TemplateRef<any>>('couponInstructionModal');
  private readonly couponInputField = viewChild.required<CouponInputComponent>('couponInputField');

  isCouponSelected(coupon: Coupon, index: number): boolean {
    if(coupon.offerType === CouponOfferType.ADVANCED && !this.order().dummy) {
      return this.order().specials?.some((special: OrderedSpecial) => special.specialType === 'Coupon' && special.special === coupon.name) || (this.unsubmittedCoupon()?.code === coupon.code) || coupon.status === CouponStatus.APPLIED;
    }
    return coupon.status === CouponStatus.APPLIED || this.loadingCouponIndex === index || (this.unsubmittedCoupon()?.code === coupon.code && this.unsubmittedCoupon()?.index === index);
  }

  ngOnInit(): void {
    this.isTempApplied = !!this.unsubmittedCoupon();
  }

  closeModal() {
    return this.modalService.closeModal(ModalName.AddCoupon);
  }

  scrollToTop() {
    const couponFormContainer = this.couponFormContainer();
    if (couponFormContainer) {
      couponFormContainer.nativeElement.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  toggleOrderDetails() {
    const pendingCoupon = this.accountCouponsService.unsubmittedCoupon();
    this.modalService.closeModal(ModalName.AddCoupon).subscribe(() => {
      if (pendingCoupon) {
        this.accountCouponsService.removeUnsubmittedCoupon();
        this.accountCouponsService.addPendingApplicationCoupon({
          code: pendingCoupon.code,
          type: pendingCoupon.type,
          triggeringPoint: pendingCoupon.triggeringPoint,
          triggeringArea: pendingCoupon.triggeringArea,
        });
      }
      this.modalService.openModal(ModalName.OrderDetails, {
        showOrderEditor: this.layoutService.mobileView() || this.orderService.order().dummy,
        triggeringPoint: EcommerceTriggeringPoint.ACCOUNT_COUPON, triggeringArea: EcommerceTriggeringArea.COUPON_MODAL
      }).subscribe((value) => {
        if (value?.canceled && pendingCoupon) {
          this.accountCouponsService.removePendingApplicationCoupon();
        }
      });
    });
  }

  handleCouponWarning(details: Coupon) {
    this.couponWarning = details;
    this.modalService.openGenericModal({
      modalTitle: this.translocoService.translate('modals.coupon_conflict.title'),
      modalContent: this.couponWarningModalTemplateRef(),
      confirmButtonText: this.translocoService.translate('modals.coupon_conflict.ok'),
    })?.subscribe(()=> this.couponWarning = null);
  }

  handleInstruction() {
    this.modalService.openGenericModal({
      modalTitle: 'Coupon Details',
      modalContent: this.couponInstructionModalTemplateRef(),
      confirmButtonText: 'Close',
    });
  }

  private showTopGenericError(couponTitle?: string) {
    const title = couponTitle?.trim() || 'selected coupon';
    this.allErrors = [
      this.translocoService.translate('modals.add_coupon_modal.coupon_error', { title }),
    ];
    this.scrollToTop();
  }

  handleApplyCoupon(value: string) {
    this.couponWarningApplicationFailed = undefined;

    if(value.trim().length === 0) return;
    this.isTempApplied = false;
    this.couponInputErrors = undefined;

    const newCoupon: Coupon = {
      code: value,
    };

    if (this.order().dummy) {
      this.scrollToTop();
      this.accountCouponsService.addUnsubmittedCoupon({code: value, triggeringArea: EcommerceTriggeringArea.COUPON_MODAL, triggeringPoint: EcommerceTriggeringPoint.COUPON_INPUT});
      this.couponInputField().couponValue = '';
      this.isTempApplied = true;
      return;
    } else {
      this.accountCouponsService.removeUnsubmittedCoupon();
    }

    this.orderService
      .addCoupon(newCoupon, EcommerceTriggeringPoint.COUPON_INPUT, EcommerceTriggeringArea.COUPON_MODAL)
      .pipe(concatMap((response) =>
        this.orderService.fetchOrder().pipe( map(() => response))
      ))
      .subscribe({
        next: (res) => {
          if(res.body?.errorType === CouponStatus.INCOMPATIBLE && res.body?.bestCouponCode)  {
            this.handleCouponWarning(res.body)
            return;
          }
          if(res.body?.errorType === CouponStatus.APPLICATION_FAILED)  {
             this.couponWarningApplicationFailed =  {
                type: OrderWarningType.ORDER,
                text: res.body.error,
              };
              this.couponResponse = res.body;
              this.accountCouponsService.addPendingApplicationCoupon({
                code: newCoupon.code ?? '',
                triggeringPoint: EcommerceTriggeringPoint.COUPON_INPUT,
                triggeringArea: EcommerceTriggeringArea.COUPON_MODAL,
                error: res.body?.error,
              });
            return;
          }
          this.scrollToTop();
          this.toastService.showSuccess(this.translocoService.translate('modals.add_coupon_modal.success_message'))
          this.modalService.closeModal(ModalName.AddCoupon);
        },
        error: (err) => {
          if (!err.error) {
            this.couponInputErrors = ['Network error'];
          } else {
            if (err.error?.errorType === CouponStatus.APPLICATION_FAILED) {
              this.accountCouponsService.addPendingApplicationCoupon({
                code: newCoupon.code ?? '',
                triggeringPoint: EcommerceTriggeringPoint.COUPON_INPUT,
                triggeringArea: EcommerceTriggeringArea.COUPON_MODAL,
                error: err?.error?.error,
              });
            }
            const parsedError = this.errorsService.parseErrorResponse(err);
            this.couponInputErrors = parsedError.allErrors;
            this.auditService.createAudit(() => `Applied Coupon: ${value}, Error Type: ${err.error.errorType}, Error:${err.error.error}`);
          }
        },
      });
  }

  _handleSpecial(selectedCoupon: Coupon) {
    this.orderService.addCoupon({
      code: selectedCoupon.code,
      type: selectedCoupon?.type,
    }).pipe(
      catchError((err) => {
        if (err.status === 409) {
          return of({
            body: {
              errorType: err.error?.errorType,
              name: selectedCoupon.name ?? selectedCoupon.code ?? '',
            },
          });
        }
        if (err.error?.errorType === CouponStatus.APPLICATION_FAILED) {
          return of({...err.error, ...selectedCoupon});
        } else if (err.error?.errorType) {
          return of(err.error);
        } else {
          this.loadingCouponIndex = -1;
          this.showTopGenericError(selectedCoupon.name ?? selectedCoupon.code);
          return of([]);
        }
    }),
      filter((response: any) => {
        const coupon = response.body;
        if (coupon.errorType === CouponStatus.APPLICATION_FAILED
          ) {
            return true;
        } else if (coupon.errorType) {
          this.loadingCouponIndex = -1;
          this.allErrors = [coupon.error];
          this.scrollToTop();
        } else {
          this.loadingCouponIndex = -1;
          this.toastService.showSuccess(this.translocoService.translate('modals.add_coupon_modal.success_message'));
          this.scrollToTop();
          this.orderService.fetchOrder().subscribe();
        }
        return false;

      }),
      switchMap((coupon) => this.orderService.removePendingSpecial(coupon.name, EcommerceTriggeringPoint.ACCOUNT_COUPON, EcommerceTriggeringArea.COUPON_MODAL).pipe(map(() => coupon))),
      switchMap(() => this.specialWizardService.fetchSpecial(this.order(), true, selectedCoupon.code ?? '')),
    ).subscribe({
      next: (specialWizard) => {
         if ((specialWizard?.steps?.length ?? 0) > 0) {
          this.closeModal().subscribe(() => {
            this.modalService.openModal(ModalName.SpecialItemEditor, {triggeringPoint: EcommerceTriggeringPoint.ACCOUNT_COUPON, triggeringArea: EcommerceTriggeringArea.COUPON_MODAL}).subscribe((value) => {
              if (value?.canceled) {
                this.modalService.openModal(ModalName.AddCoupon);
              }
            });
          });
        } else {
          this.loadingCouponIndex = -1;
          this.showTopGenericError(selectedCoupon.name ?? selectedCoupon.code);
        }
      },
      error: () => {
        this.loadingCouponIndex = -1;
        this.showTopGenericError(selectedCoupon.name ?? selectedCoupon.code);
      }
    });
  }

  handleSelectCoupon(selected: boolean, coupon: Coupon, index: number) {
    this.isTempApplied = false;
    this.allErrors = [];
    this.loadingCouponIndex = index;

    if (selected) {
      if (this.order().dummy) {
        if (coupon.offerType === CouponOfferType.ADVANCED && !coupon.isAdjustment) {
          this.modalService.openModal(ModalName.OrderDetails, {showOrderEditor: true, forceOpenModal: true, triggeringPoint: EcommerceTriggeringPoint.ACCOUNT_COUPON, triggeringArea: EcommerceTriggeringArea.COUPON_MODAL}).subscribe((value) => {
            if(value?.canceled) {
              this.loadingCouponIndex = -1;
            } else {
              this._handleSpecial(coupon);
            }
          });
          return;
        }
        this.scrollToTop();
        this.accountCouponsService.addUnsubmittedCoupon({code: coupon.code ?? '', index: index, type: coupon.type, offerType: coupon.offerType, status: coupon.status, triggeringPoint: EcommerceTriggeringPoint.ACCOUNT_COUPON, triggeringArea: EcommerceTriggeringArea.COUPON_MODAL});
        this.isTempApplied = true;
        this.loadingCouponIndex = -1;
        return;
      } else {
        this.accountCouponsService.removeUnsubmittedCoupon();
      }
      if(coupon.offerType === CouponOfferType.ADVANCED && !coupon.isAdjustment){
        this._handleSpecial(coupon);
        return;
      }
      return this.orderService
        .addCoupon(coupon, EcommerceTriggeringPoint.ACCOUNT_COUPON, EcommerceTriggeringArea.COUPON_MODAL)
        .pipe(
          concatMap((res) => this.orderService.fetchOrder().pipe(map(() => res))),
          tap(() => {
            if (this.authService.state().account?.signUpForLoyalty) {
              this.loyaltyService.fetchLoyaltyProgram().subscribe();
            }
          })
        )
        .subscribe({
          next: (res) => {
            if (res.body?.errorType === CouponStatus.APPLICATION_FAILED) {
              this.accountCouponsService.addPendingApplicationCoupon({
                code: coupon.code ?? '',
                type: coupon.type,
                triggeringPoint: EcommerceTriggeringPoint.ACCOUNT_COUPON,
                triggeringArea: EcommerceTriggeringArea.COUPON_MODAL,
                error: res.body?.error,
              });
              this.loadingCouponIndex = -1;
              return;
            }
            this.scrollToTop();
            this.loadingCouponIndex = -1;
            this.toastService.showSuccess(this.translocoService.translate('modals.add_coupon_modal.success_message'));
          },
          error: (err) => {
            this.scrollToTop();
            if (err.error?.errorType !== CouponStatus.APPLICATION_FAILED) {
              this.accountCouponsService.reloadCoupons();
            }
            this.loadingCouponIndex = -1;
            if (!err.error) {
              this.allErrors = ['Network error'];
            } else {
              if (err.error?.errorType === CouponStatus.APPLICATION_FAILED) {
                this.accountCouponsService.addPendingApplicationCoupon({
                  code: coupon.code ?? '',
                  type: coupon.type,
                  triggeringPoint: EcommerceTriggeringPoint.ACCOUNT_COUPON,
                  triggeringArea: EcommerceTriggeringArea.COUPON_MODAL,
                  error: err?.error?.error,
                });
              }
              const parsedError = this.errorsService.parseErrorResponse(err);
              this.allErrors = parsedError.allErrors;
            }
          },
        });
    } else {
      this.auditService.createAudit(() => `Coupon: ${coupon.name} with code: ${coupon.code}, has been unclaimed`);

      if (this.order().dummy) {
        this.accountCouponsService.removeUnsubmittedCoupon();
        this.loadingCouponIndex = -1
      }
      const appliedAdjustment: Adjustment | undefined =
        this.order().adjustments?.find(
          (adjustment: Adjustment) => (adjustment.couponCode === coupon.code)
        );
      const appliedSpecial: OrderedSpecial | undefined =
        this.order().specials?.find(
          (special: OrderedSpecial) => (special.couponCode === coupon.code)
        );

      if (appliedAdjustment) {
        return this.orderService
          .removeAdjustment(appliedAdjustment)
          .pipe(mergeMap(() => this.loyaltyService.fetchLoyaltyProgram()))
          .subscribe({complete: () => this.loadingCouponIndex = -1});
      } else if (
        !appliedAdjustment &&
        !appliedSpecial &&
         !this.order().dummy &&
         (!coupon.instanceCode || this.order().pendingSpecial)
      ) {
        return this.orderService.removePendingSpecial(coupon.name ?? coupon.code ?? '', EcommerceTriggeringPoint.ACCOUNT_COUPON, EcommerceTriggeringArea.COUPON_MODAL).pipe(mergeMap(() => this.loyaltyService.fetchLoyaltyProgram()))
        .subscribe({complete: () => this.loadingCouponIndex = -1});
      } else if (appliedSpecial) {
        return this.orderSpecialService
          .removeSpecialFromOrder(appliedSpecial, EcommerceTriggeringPoint.ACCOUNT_COUPON, EcommerceTriggeringArea.COUPON_MODAL)
          .subscribe({complete: () => this.loadingCouponIndex = -1});

      } else if (coupon.instanceCode) {
        return this.accountCouponsService.unredeemOptionalLoyaltyCoupon(coupon.instanceCode, coupon?.name ?? coupon.instanceCode, EcommerceTriggeringPoint.ACCOUNT_COUPON, EcommerceTriggeringArea.COUPON_MODAL)
          .pipe(mergeMap(() => this.loyaltyService.fetchLoyaltyProgram()))
          .subscribe({
            error: () => {
            this.accountCouponsService.reloadCoupons();
          },
          complete: () => this.loadingCouponIndex = -1});
      } else {
        this.loadingCouponIndex = -1
        return true;
      }
    }
  }
}
